import { format, isValid, parse } from "date-fns";
import React from "react";
import Button from "../../common/Button/Button";
import type { PopoverHandle } from "../../common/Popover/Popover";
import TextInput, { type TextInputProps } from "../TextInput/TextInput";
import DateTimePicker from "./DateTimePicker";

const DATE_INPUT_FORMAT = "yyyy-MM-dd";
const TIME_INPUT_FORMAT = "HH:mm";
const DATE_TIME_INPUT_FORMAT = `${DATE_INPUT_FORMAT} ${TIME_INPUT_FORMAT}`;
const FALLBACK_LOCALE_DATE_FORMAT = "yyyy-MM-dd";

const escapeDateFormatLiteral = (value: string) => `'${value.replace(/'/g, "''")}'`;

const getLocaleDateInputFormat = () => {
  if (typeof Intl === "undefined") return FALLBACK_LOCALE_DATE_FORMAT;

  const parts = new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).formatToParts(new Date(2006, 10, 22));

  const formatParts = parts
    .map((part) => {
      if (part.type === "day") return "dd";
      if (part.type === "month") return "MM";
      if (part.type === "year") return "yyyy";
      return escapeDateFormatLiteral(part.value);
    })
    .join("");

  return formatParts || FALLBACK_LOCALE_DATE_FORMAT;
};

const getDateInputFormats = (localeDateFormat: string) =>
  Array.from(
    new Set([
      localeDateFormat,
      DATE_INPUT_FORMAT,
      "yyyy/M/d",
      "yyyy.M.d",
      "yyyy M d",
      "dd/MM/yyyy",
      "d/M/yyyy",
      "dd-MM-yyyy",
      "d-M-yyyy",
      "dd.MM.yyyy",
      "d.M.yyyy",
      "MM/dd/yyyy",
      "M/d/yyyy",
      "MM-dd-yyyy",
      "M-d-yyyy",
      "MM.dd.yyyy",
      "M.d.yyyy",
      "d MMM yyyy",
      "dd MMM yyyy",
      "d MMMM yyyy",
      "dd MMMM yyyy",
      "MMM d yyyy",
      "MMM d, yyyy",
      "MMMM d yyyy",
      "MMMM d, yyyy",
    ]),
  );

const parseWithFormats = (value: string, formats: string[]) => {
  for (const formatString of formats) {
    const parsed = parse(value, formatString, new Date());
    if (isValid(parsed)) return parsed;
  }
  return null;
};

const parseDateValue = (
  value: string,
  dateOnly: boolean,
  currentValue?: Date | null,
  localeDateFormat = DATE_INPUT_FORMAT,
) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;
  const dateInputFormats = getDateInputFormats(localeDateFormat);

  if (dateOnly) {
    return parseWithFormats(trimmedValue, dateInputFormats);
  }

  const parsedDateTime = parseWithFormats(trimmedValue, [
    ...dateInputFormats.flatMap((dateFormat) => [`${dateFormat} ${TIME_INPUT_FORMAT}`, `${dateFormat} H:mm`]),
    DATE_TIME_INPUT_FORMAT,
    "yyyy-MM-dd H:mm",
    "yyyy-MM-dd'T'HH:mm",
    "yyyy-MM-dd'T'HH:mm:ss",
  ]);
  if (parsedDateTime) return parsedDateTime;

  const parsedDate = parseWithFormats(trimmedValue, dateInputFormats);
  if (!parsedDate) return null;

  if (!currentValue) return parsedDate;

  parsedDate.setHours(
    currentValue.getHours(),
    currentValue.getMinutes(),
    currentValue.getSeconds(),
    currentValue.getMilliseconds(),
  );
  return parsedDate;
};

const applyTimeToDate = (date: Date, time: string) => {
  const parsed = parse(time, TIME_INPUT_FORMAT, new Date());
  const nextDate = new Date(date);
  if (!isValid(parsed)) return nextDate;
  nextDate.setHours(parsed.getHours(), parsed.getMinutes(), 0, 0);
  return nextDate;
};

const isSameDateTime = (left: Date | null | undefined, right: Date | null | undefined, dateOnly: boolean) => {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return (
    format(left, dateOnly ? DATE_INPUT_FORMAT : DATE_TIME_INPUT_FORMAT) ===
    format(right, dateOnly ? DATE_INPUT_FORMAT : DATE_TIME_INPUT_FORMAT)
  );
};

export type DateTimeInputSerialize<TValue> = {
  to: (value: Date | null) => TValue;
  from: (value: TValue | null | undefined) => Date | null;
};

export interface DateTimeInputProps<TValue = string>
  extends Omit<TextInputProps, "value" | "onValueChange" | "onChange"> {
  value?: TValue;
  onValueChange?: (value: TValue) => void;
  serialize?: DateTimeInputSerialize<TValue> | false;
  inputValue?: string;
  onInputValueChange?: (value: string) => void;
  dateOnly?: boolean;
  allowNow?: boolean;
  allowArbitrary?: boolean;
  now?: boolean;
  onNowChange?: (value: boolean) => void;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: Date[];
}

const DateTimeInput = <TValue = string>({
  value,
  onValueChange,
  serialize,
  inputValue: controlledInputValue,
  onInputValueChange,
  dateOnly = false,
  allowNow = false,
  allowArbitrary = false,
  now = false,
  onNowChange,
  minDate,
  maxDate,
  disabledDates,
  disabled,
  placeholder,
  clearable,
  onClear,
  clearAriaLabel,
  ...otherProps
}: DateTimeInputProps<TValue>) => {
  const popoverRef = React.useRef<PopoverHandle | null>(null);
  const localeDateFormat = React.useMemo(() => getLocaleDateInputFormat(), []);
  const defaultSerialize = React.useMemo<DateTimeInputSerialize<string>>(
    () => ({
      to: (nextValue) => (nextValue ? format(nextValue, dateOnly ? DATE_INPUT_FORMAT : DATE_TIME_INPUT_FORMAT) : ""),
      from: (nextValue) => {
        if (!nextValue) return null;
        return parseDateValue(String(nextValue), dateOnly, undefined, localeDateFormat);
      },
    }),
    [dateOnly, localeDateFormat],
  );
  const resolvedSerialize = (serialize === undefined ? defaultSerialize : serialize) as
    | DateTimeInputSerialize<TValue>
    | false;
  const dateValue = React.useMemo(() => {
    if (resolvedSerialize) return resolvedSerialize.from(value as TValue);
    return (value ?? null) as Date | null;
  }, [resolvedSerialize, value]);
  const [uncontrolledInputValue, setUncontrolledInputValue] = React.useState("");
  const isEditingRef = React.useRef(false);
  const lastCommittedValueRef = React.useRef<Date | null>(dateValue ?? null);
  const inputValue = controlledInputValue ?? uncontrolledInputValue;
  const displayFormat = dateOnly ? localeDateFormat : `${localeDateFormat} ${TIME_INPUT_FORMAT}`;
  const timeValue = dateValue ? format(dateValue, TIME_INPUT_FORMAT) : "";
  const serializeValue = React.useCallback(
    (nextValue: Date | null): TValue => {
      if (resolvedSerialize) return resolvedSerialize.to(nextValue) as TValue;
      return nextValue as TValue;
    },
    [resolvedSerialize],
  );
  const commitValue = React.useCallback(
    (nextValue: Date | null) => {
      const nextSerializedValue = serializeValue(nextValue);
      if (Object.is(value, nextSerializedValue)) return;
      onValueChange?.(nextSerializedValue);
    },
    [onValueChange, serializeValue, value],
  );
  const setInputText = React.useCallback(
    (nextValue: string, options?: { notify?: boolean }) => {
      if (controlledInputValue === undefined) setUncontrolledInputValue(nextValue);
      if (options?.notify !== false) onInputValueChange?.(nextValue);
    },
    [controlledInputValue, onInputValueChange],
  );

  React.useEffect(() => {
    const parsedInputValue = parseDateValue(inputValue, dateOnly, dateValue, localeDateFormat);
    const valueMatchesLastCommittedValue = isSameDateTime(dateValue, lastCommittedValueRef.current, dateOnly);
    const inputMatchesLastCommittedValue =
      parsedInputValue &&
      isSameDateTime(parsedInputValue, lastCommittedValueRef.current, dateOnly) &&
      valueMatchesLastCommittedValue;
    const shouldPreserveInputValue =
      allowArbitrary ||
      (parsedInputValue && (isSameDateTime(parsedInputValue, dateValue, dateOnly) || inputMatchesLastCommittedValue));
    const shouldSyncInputValue = !isEditingRef.current || !valueMatchesLastCommittedValue;

    if (now) {
      if (inputValue !== "now") setInputText("now", { notify: false });
      return;
    }

    if (shouldSyncInputValue && !shouldPreserveInputValue) {
      setInputText(dateValue ? format(dateValue, displayFormat) : "", { notify: false });
    }
  }, [allowArbitrary, dateOnly, dateValue, displayFormat, inputValue, localeDateFormat, now, setInputText]);

  const commitInputValue = (nextValue: string) => {
    const normalizedInputValue = nextValue.trim().toLowerCase();

    if (allowNow && now && "now".startsWith(normalizedInputValue)) {
      lastCommittedValueRef.current = null;
      onNowChange?.(false);
      setInputText("");
      onValueChange?.("" as TValue);
      return;
    }

    if (allowNow && normalizedInputValue === "now") {
      lastCommittedValueRef.current = null;
      onNowChange?.(true);
      return;
    }

    if (now) onNowChange?.(false);

    const parsed = parseDateValue(nextValue, dateOnly, dateValue, localeDateFormat);
    if (parsed) {
      lastCommittedValueRef.current = parsed;
      commitValue(parsed);
      return;
    }
    if (allowArbitrary) return;

    if (!nextValue) {
      lastCommittedValueRef.current = null;
      commitValue(null);
    }
  };

  const handleInputValueChange = (nextValue: string | undefined) => {
    const resolvedValue = nextValue ?? "";
    isEditingRef.current = true;
    setInputText(resolvedValue);
    commitInputValue(resolvedValue);
  };

  const handleDateSelect = (date: Date | null | undefined) => {
    if (!date) {
      commitValue(null);
      return;
    }
    const nextValue = dateOnly ? date : applyTimeToDate(date, timeValue || "00:00");
    isEditingRef.current = false;
    if (now) onNowChange?.(false);
    setInputText(format(nextValue, displayFormat));
    lastCommittedValueRef.current = nextValue;
    commitValue(nextValue);
  };

  const handleNowClick = () => {
    isEditingRef.current = false;
    lastCommittedValueRef.current = null;
    if (now) {
      onNowChange?.(false);
      setInputText("");
      commitValue(null);
      return;
    }
    onNowChange?.(true);
    setInputText("now");
  };
  const handleClear = React.useCallback(() => {
    isEditingRef.current = false;
    lastCommittedValueRef.current = null;
    if (now) onNowChange?.(false);
    setInputText("");
    commitValue(null);
    onClear?.();
  }, [commitValue, now, onClear, onNowChange, setInputText]);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    isEditingRef.current = false;
    commitInputValue(inputValue);
    otherProps.onBlur?.(event);
  };

  return (
    <DateTimePicker
      mode="focus"
      placement="bottom-start"
      interactable
      handleRef={popoverRef}
      value={dateValue}
      onValueChange={handleDateSelect}
      dateOnly={dateOnly}
      minDate={minDate}
      maxDate={maxDate}
      disabledDates={disabledDates}
      disabled={disabled}
    >
      <DateTimePicker.Reference asChild>
        <TextInput
          {...otherProps}
          value={inputValue}
          disabled={disabled}
          placeholder={placeholder ?? (dateOnly ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm")}
          onValueChange={handleInputValueChange}
          onBlur={handleBlur}
          clearable={clearable}
          onClear={handleClear}
          clearAriaLabel={clearAriaLabel}
          endAdornment={
            <>
              {otherProps.endAdornment}
              {allowNow ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  intent="secondary"
                  label="Now"
                  selected={now}
                  aria-pressed={now}
                  onClick={handleNowClick}
                  disabled={disabled}
                />
              ) : null}
            </>
          }
        />
      </DateTimePicker.Reference>
      <DateTimePicker.Content />
    </DateTimePicker>
  );
};

export default DateTimeInput;
